import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch, RCCTextArea, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { ModalCenter } from "src/components/Modal/styles";
import Select from "src/components/Select";
import ModalFormulaAssistant from "src/components/ModalFormulaAssistant";
import { IRefModalFormulaAssistant } from "src/components/ModalFormulaAssistant/interface";
import { IModalAddChangeDeleteLines, IOftbm003, IOftbm005, IRefModalAddChangeDeleteLines } from "./interface";
import AddAndChangeSchema from "./schemas";
import { IOftbm002 } from "../../interface";
import CompositionValuesOfLine from "../CompositionValuesOfLine";

const returnOptions = (origin: number, translate: (text: string, objKeys?: object | undefined) => string) => {
	const subsidiary = {
		label: translate("Unidade/Filial"),
		value: "2"
	};
	const salesChannel = {
		label: translate("Canal de venda"),
		value: "3"
	};
	const productLine = {
		label: translate("Linha de produto"),
		value: "4"
	};
	const variableExpenses = {
		label: translate("Despesas variáveis"),
		value: "5"
	};
	const salesDeduction = {
		label: translate("Dedução de vendas"),
		value: "6"
	};
	const centerCost = {
		label: translate("Centro de custo"),
		value: "7"
	};
	const account = {
		label: translate("Conta"),
		value: "8"
	};
	const otherAccounts = {
		label: translate("Outras contas"),
		value: "9"
	};
	const variableCosts = {
		label: translate("Custos variáveis"),
		value: "10"
	};
	switch (origin)
	{
	case 1:
		return [salesChannel, productLine];
	case 2:
		return [salesChannel, productLine, salesDeduction];
	case 3:
		return [productLine, variableCosts];
	case 4:
		return [salesChannel, productLine, variableExpenses];
	case 5:
		return [centerCost, account];
	case 6:
		return [centerCost, otherAccounts];
	case 91:
		return [subsidiary];
	default:
		return [];
	}
};

const ModalAddChangeDeleteLines: React.ForwardRefRenderFunction<IRefModalAddChangeDeleteLines, IModalAddChangeDeleteLines> = ({
	ofm001Id,
	budgetPlanId
},ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const {setLoading} = useLoaderBackdrop();

	const refModalFormulaAssistant = useRef<IRefModalFormulaAssistant>(null);

	const [incomeLine, setIncomeLine] = useState<IOftbm002>({} as IOftbm002);

	// Modal Add/Change
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const [showQuantity, setShowQuantity] = useState(false);
	const [showFormula, setShowFormula] = useState(false);
	const [isAdd, setIsAdd] = useState(true);
	const onCallbackFunction = useRef<Function>(() => {});
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange,
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange,
		control: controlAddOrChange,
		setValue: setValueAddOrChange
	} = useForm();

	const actionButtonAdd = async (onCallback: (dt: IOftbm002) => void) => {
		resetAddOrChange(); 
		onCallbackFunction.current = onCallback;
		setIsAdd(true);
		const obj = { ofm002Id: 0, ofm002Origem: 1 } as IOftbm002;
		setIncomeLine(obj);
		setShowQuantity(true);
		setShowFormula(false);
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = async (id: number, onCallback: (dt: IOftbm002) => void) => {
		resetAddOrChange(); 
		try{
			setLoading(true);
			const result = await RCCServicesApi.getRequest({
				method: "IncomeStatement/SelectLine",
				urlParameters: {
					id
				}
			});
			onCallbackFunction.current = onCallback;
			setIncomeLine(result.data);
			setShowQuantity(result.data.ofm002Origem === 1);
			setShowFormula(result.data.ofm002Origem === 90);
			setIsAdd(false);
			setLoading(false);
			modalAddOrChange.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar linha do demonstrativo"), addToast);
		}
	};

	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			
			await AddAndChangeSchema(translate).validate({...data}, {
				abortEarly: false
			});

			const dataForm = {
				...data,
				ofm002Baseav: data.ofm002Baseav ? "S" : "N",
				ofm002Destacalinha: data.ofm002Destacalinha ? "S" : "N",
				ofm002Mostralinha: data.ofm002Mostralinha ? "S" : "N",
				ofm002Ofm001Id: ofm001Id
			};

			const response = isAdd ? await RCCServicesApi.postRequest({
				method: "IncomeStatement/InsertIncomeLine",
				body: { ...dataForm }
			}) : await RCCServicesApi.putRequest({
				method: "IncomeStatement/EditIncomeLine",
				body: { ...dataForm }
			});

			addToast({
				color: "green",
				description: translate("Linha do demonstrativo alterado com sucesso"),
				title: translate("Sucesso")
			});

			onCallbackFunction.current(response.data);
			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
				
			standardErrorMessages(err, translate(isAdd ? "Erro ao adicionar linha do demonstrativo" : "Erro ao alterar linha do demonstrativo"), addToast);
		}
	};

	const actionButtonDelete = async (id: number, onCallback: (id: number) => void) => {
		confirm(
			translate("Excluir linha do demonstrativo"),
			`${translate("Deseja realmente excluir esta linha do demonstrativo")}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "IncomeStatement/DeleteLine",
					urlParameters: {
						id
					}
				});
    
				addToast({
					color: "green",
					description: translate("Linha do demonstrativo excluída com sucesso"),
					title: translate("Sucesso")
				});

				onCallback(id);
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir linha do demonstrativo"), addToast);
			},
			true
		);
	};

	// Modal Drill Down
	const [drillDown, setDrillDown] = useState<IOftbm005>({} as IOftbm005);
	const modalDrillDown = useRef<RCCRefModalProps>(null);
	const {
		handleSubmit: handleSubmitDrillDown,
		control: controlDrillDown,
		reset: resetDrillDown,
		register: registerDrillDown
	} = useForm();
	const [optionsSelect, setOptionsSelect] = useState([{
		label: translate("Unidade/Filial"),
		value: "2"
	},{
		label: translate("Canal de venda"),
		value: "3"
	},{
		label: translate("Linha de produto"),
		value: "4"
	},{
		label: translate("Despesas variáveis"),
		value: "5"
	},{
		label: translate("Dedução de vendas"),
		value: "6"
	},{
		label: translate("Centro de custo"),
		value: "7"
	},{
		label: translate("Conta"),
		value: "8"
	},{
		label: translate("Outras contas"),
		value: "9"
	}]);

	const actionButtonDrillDown = async (id: number, origin: number) => {
		resetDrillDown();
		try{
			setLoading(true);
			const result = await RCCServicesApi.getRequest({
				method: "IncomeStatement/SelectDrillDownLine",
				urlParameters: {
					id,
					ofm001Id
				}
			});
			setOptionsSelect(returnOptions(origin, translate));
			const oftbm005: IOftbm005 = result.data && result.data !== null ? result.data as IOftbm005 : { ofm005Id: 0 } as IOftbm005;
			oftbm005.ofm005Ofm002Id = id;
			setDrillDown(oftbm005);
			setLoading(false);
			modalDrillDown.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar drilldown da linha do demonstrativo"), addToast);
		}
	};

	const submitDrillDown = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalDrillDown.current?.setLoadingButton();
			
			const dataForm: IOftbm005 = {
				...data,
				ofm005Ofm002Id: drillDown.ofm005Ofm002Id,
				ofm005Hierarquia: data.ofm005Hierarquia.toString().length === 0 ? 0 : data.ofm005Hierarquia
			};

			await RCCServicesApi.putRequest({
				method: "IncomeStatement/UpdateDrillDown",
				body: { ...dataForm }
			});

			addToast({
				color: "green",
				description: translate("Drilldown atualizado com sucesso"),
				title: translate("Sucesso")
			});

			modalDrillDown.current?.resetLoadingButton();
			modalDrillDown.current?.closeModal();
		}catch(err){
			modalDrillDown.current?.resetLoadingButton();
			standardErrorMessages(err, translate("Erro ao atualizar drilldown da linha do demonstrativo"), addToast);
		}
	};

	// Modal values
	const [compositionValues, setCompositionValues] = useState<IOftbm003[]>([]);
	const [originCompositionValues, setOriginCompositionValues] = useState(0);
	const [subtitleValues, setSubtitleValues] = useState("");
	const [useApportionmentCC, setUseApportionmentCC] = useState(true);
	const ofm002Id = useRef(0);
	const modalValues = useRef<RCCRefModalProps>(null);
	const {
		handleSubmit: handleSubmitValues,
		control: controlValues,
		reset: resetValues,
		register: registerValues,
		setValue: setValueValues
	} = useForm();

	const actionButtonValues = async (id: number, origin: number, name: string, useApportionment: boolean) => {
		resetValues();
		try{
			setLoading(true);
			const result = await RCCServicesApi.getRequest({
				method: "IncomeStatement/SelectCompositionValuesLine",
				urlParameters: {
					id,
					ofm001Id,
					budgetPlanId
				}
			});
			ofm002Id.current = id;
			setSubtitleValues(`${translate("Linha")}: ${name}`);
			setCompositionValues(result.data);
			setOriginCompositionValues(origin);
			setUseApportionmentCC(useApportionment);
			setLoading(false);
			modalValues.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar composição de valores da linha do demonstrativo"), addToast);
		}
	};

	const submitValues = async (data: any) => {
		try{
			modalValues.current?.setLoadingButton();
			
			await RCCServicesApi.putRequest({
				method: "IncomeStatement/UpdateCompositionValuesLine",
				body: { values: data.oftbm003, ofm002Id: ofm002Id.current }
			});

			addToast({
				color: "green",
				description: translate("Composição de valores atualizados com sucesso"),
				title: translate("Sucesso")
			});

			modalValues.current?.resetLoadingButton();
			modalValues.current?.closeModal();
		}catch(err){
			modalValues.current?.resetLoadingButton();
			standardErrorMessages(err, translate("Erro ao atualizar composição de valores"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete,
		actionButtonDrillDown,
		actionButtonValues
	}));

	return (
		<>
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar linha do demonstrativo") : translate("Alterar linha do demonstrativo"),
					icon: {
						name: isAdd ? "plus" : "pencil"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={`${ModalCenter} height: 532px;`}
				withoutForm
			>
				<RCCGrid container item direction="column" alignItems="center">
					<input 
						type="text"
						value={incomeLine.ofm002Id}
						{...registerAddOrChange("ofm002Id")} 
						style={{ display: "none" }} 
						readOnly
					/>
					<RCCGrid container item xs={12} sm={10}>
						<RCCInput 
							name="ofm002Descricao"
							register={registerAddOrChange}
							label={translate("Descrição")}
							maxLength={200}
							defaultValue={incomeLine.ofm002Descricao}
							error={errorsAddOrChange.ofm002Descricao}
							required
						/>
					</RCCGrid>
					<RCCGrid container item xs={12} sm={10}>
						<RCCGrid container item xs={6}>
							<Select 
								name="ofm002Origem"
								control={controlAddOrChange}
								label={translate("Origem")}
								defaultValue={incomeLine.ofm002Origem ? incomeLine.ofm002Origem.toString() : "1"}
								onChangeCapture={(e: any) => {
									if(e.value === "1")
										setValueAddOrChange("ofm002Formato", "V");
								
									setShowFormula(e.value === "90");
									setShowQuantity(e.value === "1");
								}}
								options={[{
									label: translate("Receita de vendas"),
									value: "1"
								},{
									label: translate("Dedução de vendas"),
									value: "2"
								},{
									label: translate("Custos variáveis"),
									value: "3"
								},{
									label: translate("Despesas variáveis"),
									value: "4"
								},{
									label: translate("Despesas operacionais"),
									value: "5"
								},{
									label: translate("Outras despesas"),
									value: "6"
								},{
									label: translate("Fórmula"),
									value: "90"
								},{
									label: translate("Impostos"),
									value: "91"
								}]}
								styleInline="padding-right: 4px;"
							/>
						</RCCGrid>
						<RCCGrid container item xs={6}>
							<Select 
								name="ofm002Formato"
								control={controlAddOrChange}
								label={translate("Formato")}
								defaultValue={incomeLine.ofm002Formato ? incomeLine.ofm002Formato.toString() : "V"}
								options={showQuantity ? [{
									label: translate("Valor"),
									value: "V"
								},{
									label: translate("Percentual"),
									value: "P"
								},{
									label: translate("Quantidade"),
									value: "Q"
								}] : [{
									label: translate("Valor"),
									value: "V"
								},{
									label: translate("Percentual"),
									value: "P"
								}]}
								styleInline="padding-left: 4px;"
							/>	
						</RCCGrid>
					</RCCGrid> 
					{showFormula && (
						<RCCGrid container item alignItems="center" justifyContent="flex-end">
							<RCCGrid container item xs={11} sm={10}>
								<RCCTextArea 
									register={registerAddOrChange}
									name="ofm002Formula"
									label={translate("Fórmula ou integração")}
									defaultValue={incomeLine.ofm002Formula}
									error={errorsAddOrChange.ofm002Formula}
									required
									disabled
								/>
							</RCCGrid>
							<RCCGrid container item xs={1}>
								<RCCTooltip
									text={translate("Abrir assistente de fórmulas")}
									color="grey"
									position="left">

									<RCCButton
										icon={{name: "xSquare"}}
										color="grey"
										onClick={() => refModalFormulaAssistant.current?.openModal(ofm001Id, incomeLine.ofm002Formula, (value: string) => {
											setValueAddOrChange("ofm002Formula", value);
										}, incomeLine.ofm002Id)}
										layout="isRounded"
										size="sm"
									/>
								</RCCTooltip>
							</RCCGrid>
						</RCCGrid>
					)}
					<RCCGrid container item xs={12} sm={10}>
						<RCCSwitch 
							name="ofm002Mostralinha"
							register={registerAddOrChange}
							label={translate("Mostrar linha")}
							defaultChecked={incomeLine.ofm002Mostralinha !== "N"}			
						/>
					</RCCGrid>
					<RCCGrid container item xs={12} sm={10}>
						<RCCSwitch 
							name="ofm002Destacalinha"
							register={registerAddOrChange}
							label={translate("Destaca linha")}
							defaultChecked={incomeLine.ofm002Destacalinha === "S"}			
						/>
					</RCCGrid>
					<RCCGrid container item xs={12} sm={10}>
						<RCCSwitch 
							name="ofm002Baseav"
							register={registerAddOrChange}
							label={translate("Base para análise vertical")}
							defaultChecked={incomeLine.ofm002Baseav === "S"}			
						/>
					</RCCGrid>
				</RCCGrid>
			</Modal>

			<Modal
				ref={modalDrillDown}
				header={{
					title: "Drill down",
					icon: {
						name: "arrowRight"
					}
				}}
				onSubmit={handleSubmitDrillDown(submitDrillDown)}
				styleInline={`${ModalCenter} height: 300px;`}
				withoutForm
			>
				<RCCGrid container item direction="column" alignItems="center">
					<input 
						type="text"
						value={drillDown.ofm005Id}
						{...registerDrillDown("ofm005Id")} 
						style={{ display: "none" }} 
						readOnly
					/>
					<RCCGrid container item xs={12} sm={10}>
						<Select 
							name="ofm005Hierarquia"
							control={controlDrillDown}
							label={translate("Hierarquia")}
							defaultValue={drillDown.ofm005Hierarquia ? drillDown.ofm005Hierarquia.toString() : ""}
							isClearable
							options={optionsSelect}
						/>
					</RCCGrid>
				</RCCGrid>
			</Modal>

			<Modal
				ref={modalValues}
				header={{
					title: translate("Composição de valores"),
					subtitle: subtitleValues,
					icon: {
						name: "list"
					}
				}}
				onSubmit={handleSubmitValues(submitValues)}
				withoutForm
			>
				<CompositionValuesOfLine 
					control={controlValues}
					lines={compositionValues}
					origin={originCompositionValues}
					register={registerValues}
					setValue={setValueValues}
					budgetPlanId={budgetPlanId}
					useApportionment={useApportionmentCC}
				/>
			</Modal>

			<ModalFormulaAssistant 
				ref={refModalFormulaAssistant}
				methodRequestValuesFormula="IncomeStatement/ListFormulaFields"
				showFieldFilter={false}
				showOnlyDescription
			/>
		</>
	);
};

export default forwardRef(ModalAddChangeDeleteLines);
